import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { IconChevronRight } from "@tabler/icons-react";
import { Crisp } from "crisp-sdk-web";
import useAuth from "hooks/useAuth";
import useConfig from "hooks/useConfig";
import navigation from "menu-items";
import React, { useMemo } from "react";
import { useCookies } from "react-cookie";
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "store";
import { drawerWidth } from "store/constant";
import { fetchPhotograph } from "store/slices/dashboard";
import { openDrawer } from "store/slices/menu";
import Breadcrumbs from "ui-component/extended/Breadcrumbs";
import axiosServices from "utils/axios";
import AlbumLayout from "views/albums/AlbumLayout";
import UnknownError from "views/ui-elements/UnknownError";
import Header from "./Header";
import Sidebar from "./Sidebar";

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    ...theme.typography.mainContent,
    ...(!open && {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
      [theme.breakpoints.up("md")]: {
        marginLeft: -(drawerWidth - 20),
        width: `calc(100% - ${drawerWidth}px)`,
      },
      [theme.breakpoints.down("md")]: {
        marginLeft: "20px",
        width: `calc(100% - ${drawerWidth}px)`,
        padding: "16px",
      },
      [theme.breakpoints.down("sm")]: {
        marginLeft: "10px",
        width: `calc(100% - ${drawerWidth}px)`,
        padding: "16px",
        marginRight: "10px",
      },
    }),
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.shorter,
      }),
      marginLeft: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      width: `calc(100% - ${drawerWidth}px)`,
      [theme.breakpoints.down("md")]: {
        marginLeft: "20px",
      },
      [theme.breakpoints.down("sm")]: {
        marginLeft: "10px",
      },
    }),
  })
);

const MainLayout = (props) => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("lg"));

  const dispatch = useDispatch();
  const { drawerOpen } = useSelector((state) => state.menu);
  const { container } = useConfig();
  const { user } = useAuth();
  const [, setCookie] = useCookies([
    "CloudFront-Policy",
    "CloudFront-Signature",
    "CloudFront-Key-Pair-Id",
  ]);

  React.useEffect(() => {
    Crisp.configure(process.env.REACT_APP_CRISP_ID);
    if (user) {
      Crisp.user.setEmail(user.email);
    }
  }, [user]);

  React.useEffect(() => {
    dispatch(openDrawer(true));
    dispatch(fetchPhotograph());
    axiosServices
      .get("/api/v1/policy-token-eventpxl")
      .then((response) => {
        const { data } = response;
        setCookie("CloudFront-Policy", data["CloudFront-Policy"], {
          path: "/",
          domain: `.${process.env.REACT_APP_DOMAIN}`,
          secure: true,
          sameSite: "None",
        });
        setCookie("CloudFront-Signature", data["CloudFront-Signature"], {
          path: "/",
          domain: `.${process.env.REACT_APP_DOMAIN}`,
          secure: true,
          sameSite: "None",
        });
        setCookie("CloudFront-Key-Pair-Id", data["CloudFront-Key-Pair-Id"], {
          path: "/",
          domain: `.${process.env.REACT_APP_DOMAIN}`,
          secure: true,
          sameSite: "None",
        });
        setCookie("CloudFront-Policy", data["CloudFront-Policy"], {
          path: "/",
          domain: `photos.${process.env.REACT_APP_DOMAIN}`,
          secure: true,
          sameSite: "None",
        });
        setCookie("CloudFront-Signature", data["CloudFront-Signature"], {
          path: "/",
          domain: `photos.${process.env.REACT_APP_DOMAIN}`,
          secure: true,
          sameSite: "None",
        });
        setCookie("CloudFront-Key-Pair-Id", data["CloudFront-Key-Pair-Id"], {
          path: "/",
          domain: `photos.${process.env.REACT_APP_DOMAIN}`,
          secure: true,
          sameSite: "None",
        });
      })
      .catch((error) => {
        console.log("Unable to fetch policy token", error);
      });
  }, [dispatch, matchDownMd, setCookie]);

  const header = useMemo(
    () => (
      <Toolbar>
        <Header />
      </Toolbar>
    ),
    []
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.default,
          transition: drawerOpen ? theme.transitions.create("width") : "none",
        }}
      >
        {header}
      </AppBar>

      <Sidebar />
      <Main theme={theme} open={drawerOpen}>
        <Sentry.ErrorBoundary fallback={<UnknownError />}>
          {container && (
            <Container maxWidth="lg">
              <Breadcrumbs
                separator={IconChevronRight}
                navigation={navigation}
                icon
                title
                rightAlign
              />
              {props.layout ? (
                <AlbumLayout>
                  <Outlet />
                </AlbumLayout>
              ) : (
                <Outlet />
              )}
            </Container>
          )}
          {!container && (
            <>
              <Breadcrumbs
                separator={IconChevronRight}
                navigation={navigation}
                icon
                title
                rightAlign
              />
              {props.layout ? (
                <AlbumLayout>
                  <Outlet />
                </AlbumLayout>
              ) : (
                <Outlet />
              )}
            </>
          )}
        </Sentry.ErrorBoundary>
      </Main>
    </Box>
  );
};

export default MainLayout;
